import React from 'react'
import { css } from '@emotion/react'
import { colors, fontSize, fontWeight, spacing } from '@retailer-platform/shared-components'
import { type EmotionStyles } from '@retailer-platform/shared-components/src/utils/styling/types'
import { Input } from 'gin-and-tonic/common/input/Input'
import {
  type DomainMessages,
  useDomainIntl,
  useDomainMessages,
} from 'domains/partner-pick-devices/utils/domain/intl'
import { type usePartnerPickDevicesForm } from 'domains/partner-pick-devices/components/form/utils/usePartnerPickDevicesForm.hooks'
import { type PartnerPickDevicesFormData } from 'domains/partner-pick-devices/types/partnerPickDevices.types'

interface PartnerPickDevicesFormInputProps extends EmotionStyles {
  identifier: keyof PartnerPickDevicesFormData
  labelMessageId: DomainMessages
  placeholderMessageId?: DomainMessages
  errors: ReturnType<typeof usePartnerPickDevicesForm>['errors']
  errorMessageIds: Record<string, DomainMessages>
  disabled?: boolean
  type?: 'email' | 'number' | 'password' | 'text'
}

const labelStyles = css({
  color: colors.GRAYSCALE.X50,
  display: 'block',
  fontSize: fontSize.X15,
  fontWeight: fontWeight.SEMIBOLD,
  marginBottom: spacing.X8,
})

const inputOuterStyles = css({
  margin: 0,
  marginBottom: spacing.X12,
})

const inputStyles = css({
  height: 46,
  fontSize: fontSize.X15,
  // style error message
  '& + div': {
    fontSize: fontSize.X12,
    marginTop: spacing.X4,
  },
})

export const PartnerPickDevicesFormInput = React.forwardRef<
  HTMLInputElement,
  PartnerPickDevicesFormInputProps
>(
  (
    {
      identifier,
      labelMessageId,
      placeholderMessageId,
      errors,
      errorMessageIds,
      disabled,
      type,
      styles,
    },
    ref
  ) => {
    const intl = useDomainIntl()

    const { label, placeholder } = useDomainMessages({
      label: labelMessageId,
      placeholder: placeholderMessageId || labelMessageId,
    })

    const errorMessage =
      errors[identifier] &&
      intl.formatMessage({ id: errorMessageIds[errors[identifier].type] }, { fieldName: label })

    return (
      <label css={styles}>
        <span css={labelStyles}>{label}</span>
        <Input
          ref={ref}
          name={identifier}
          placeholder={placeholder}
          errorMessage={errorMessage}
          errorMessageId={errors[identifier] ? 'forms.validations.required' : null}
          disabled={disabled}
          type={type}
          outerStyles={inputOuterStyles}
          styles={inputStyles}
          data-testid={`partnerPickDevicesFormInput-${identifier}`}
        />
      </label>
    )
  }
)
